/* !
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Input,
	OnInit
} from '@angular/core';
import {
	AbstractControl,
	UntypedFormControl
} from '@angular/forms';
import {
	Router
} from '@angular/router';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	DynamicWizardComponent
} from '@dynamicComponents/dynamic-wizard/dynamic-wizard.component';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	InsuranceHelper
} from '@insurance/helpers/insurance.helper';
import {
	IInsurancePaymentSummary
} from '@insurance/interfaces/insurance-payment-summary.interface';
import {
	InsuranceService
} from '@insurance/services/insurance.service';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	IDropdownOption
} from '@shared/interfaces/application-objects/dropdown-option.interface';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IWizardContext
} from '@shared/interfaces/dynamic-interfaces/wizard-context.interface';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	ModuleService
} from '@shared/services/module.service';
import {
	SiteLayoutService
} from '@shared/services/site-layout.service';

@Component({
	selector: 'adjustment-write-off-amount',
	templateUrl: './adjustment-write-off-amount.component.html',
	styleUrls: [
		'./adjustment-write-off-amount.component.scss'
	]
})

/**
 * A component representing a wizard step to display the payment details and
 * collect an amount for a policy term write off.
 *
 * @export
 * @class AdjustmentWriteOffAmountComponent
 * @implements {OnInit}
 * @implements {IDynamicComponent<DynamicWizardComponent, IWizardContext>}
 */
export class AdjustmentWriteOffAmountComponent
implements OnInit, IDynamicComponent<DynamicWizardComponent, IWizardContext>
{
	/**
	 * Initializes an instance of the adjustment write off component.
	 *
	 * @param {Router} router
	 * The router used for navigation and url query parameter storage.
	 * @param {ActivityService} activityService
	 * The activity message service used to notify the user.
	 * @param {ModuleService} moduleService
	 * The module service used to set module changes.
	 * @param {InsuranceService} insuranceService
	 * The insurance service used to lookup insurance modules.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service used in this component.
	 * @param {SiteLayoutService} siteLayoutService
	 * The site layout service used in this component.
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public constructor(
		public router: Router,
		public activityService: ActivityService,
		public moduleService: ModuleService,
		public insuranceService: InsuranceService,
		public entityInstanceApiService: EntityInstanceApiService,
		public siteLayoutService: SiteLayoutService)
	{
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<
	 * 	DynamicWizardComponent,
	 * 	IWizardContext>}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	@Input() public context: IDynamicComponentContext<
		DynamicWizardComponent,
		IWizardContext>;

	/**
	 * Gets or sets the context active menu item current data collected on the
	 * dynamic wizard steps.
	 *
	 * @type {any}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public get contextData(): any {
		return this.context.source.activeMenuItem.currentData.data;
	}

	/**
	 * Gets the reference field configuration.
	 * 
	 * @type {FormlyFieldConfig}
	 * The reference field configuration.
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public get referenceFieldConfig(): FormlyFieldConfig {
		return this.dynamicFormlyLayout[2];
	}

	/**
	 * Gets or sets the formly layout used in implementing components.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public dynamicFormlyLayout: FormlyFieldConfig[];

	/**
	 * Gets or sets the PolicyTerm entity instance associated to the current
	 * data.
	 *
	 * @type {IEntityInstance}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public policyTermEntityInstance: IEntityInstance;

	/**
	 * Gets or sets the ledger entity instance associated to the current
	 * policy term.
	 *
	 * @type {IEntityInstance}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public ledgerEntityInstance: IEntityInstance;

	/**
	 * Gets or sets the payment association settings retrieved from
	 * the product associated to the transaction.
	 * 
	 * @type {any}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public paymentAssociationSettings: any;

	/**
	 * Implements the on initialization interface.
	 *
	 * @async
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public async ngOnInit(): Promise<void>
	{
		const currentData: any =
			this.context.source.activeMenuItem.currentData.data;

		const policyTermId: number = currentData.id;

		this.policyTermEntityInstance =
			await this.insuranceService.getPolicyTerm(
				policyTermId);

		this.ledgerEntityInstance =
			await this.insuranceService.getLedger(
				policyTermId);

		const ledgerTransactionInstances: IEntityInstance[] =
			await this.insuranceService.getLedgerTransactions(
				this.ledgerEntityInstance.id);

		const priorTermTransfer: number =
			InsuranceHelper.getPriorTermTransferBalance(
				ledgerTransactionInstances);

		const policyTermWrittenOffBalance: number =
			InsuranceHelper.getPolicyTermWrittenOffBalance(
				ledgerTransactionInstances);

		const payments: number =
			InsuranceHelper.getPaymentsBalance(
				ledgerTransactionInstances);

		const operationalFees: number =
			InsuranceHelper.getPolicyTermOperationalFeeBalance(
				this.policyTermEntityInstance,
				ledgerTransactionInstances);

		const futureIssuedTransactions: IEntityInstance[] =
			await this.insuranceService.getFutureIssuedTransactions(
				this.policyTermEntityInstance);

		this.paymentAssociationSettings =
			await this.insuranceService.getProductPaymentAssociationSettings(
				this.policyTermEntityInstance.data.productName);

		// If we are past bind, only use the draft payment schedule when
		// future issued transactions exist.
		let accountingPayments: any[] =
			this.policyTermEntityInstance
				?.data.accounting?.paymentSchedule?.payments;

		if (futureIssuedTransactions.length > 0
			&& !AnyHelper.isNull(
				accountingPayments))
		{
			const actionResponse: any =
				await this.insuranceService.generateDraftPaymentPlanSchedule(
					this.policyTermEntityInstance.id,
					InsuranceConstants.insuranceEntityTypeGroups.policyTerms);

			accountingPayments =
				actionResponse?.body?.value.payments;
		}

		const remainingPayments: IInsurancePaymentSummary[] =
			await this.insuranceService.getRemainingPaymentSummaries(
				policyTermId,
				InsuranceConstants.insuranceEntityTypeGroups.policyTerms,
				accountingPayments);
		const nextPayment: IInsurancePaymentSummary =
			await this.insuranceService.getNextInstallmentPayment(
				policyTermId,
				InsuranceConstants.insuranceEntityTypeGroups.policyTerms,
				remainingPayments,
				false);

		let accountingData: any;
		let paymentDetailsData: any;
		const minimumDue: number = nextPayment?.due ?? 0;

		if (this.policyTermEntityInstance.data.status ===
			InsuranceConstants.policyTermStatusTypes.active)
		{
			accountingData =
				this.policyTermEntityInstance.data.accounting;

			paymentDetailsData =
				{
					premium: accountingData.writtenPremium,
					fees: accountingData.writtenFees + operationalFees,
					taxes: accountingData.writtenTaxes,
					total: accountingData.writtenTotal + operationalFees,
					priorTermTransfer: priorTermTransfer,
					policyTermWrittenOffBalance: policyTermWrittenOffBalance,
					payments: payments,
					remainingBalance:
						this.ledgerEntityInstance.data.balance,
					minimumDue: minimumDue
				};
		}
		else
		{
			const policyTermTransactionInstance: IEntityInstance =
				await this.insuranceService
					.getLatestPolicyTermTransactionByPolicyTerm(
						this.policyTermEntityInstance.id);

			accountingData =
				policyTermTransactionInstance.data.accounting;

			paymentDetailsData =
				{
					premium: accountingData.directWrittenPremium,
					fees: accountingData.directWrittenFees + operationalFees,
					taxes: accountingData.directWrittenTaxes,
					total: accountingData.directWrittenTotal + operationalFees,
					priorTermTransfer: priorTermTransfer,
					policyTermWrittenOffBalance: policyTermWrittenOffBalance,
					payments: payments,
					remainingBalance:
						accountingData.directWrittenTotal
							+ priorTermTransfer
							+ payments
							+ policyTermWrittenOffBalance
							+ operationalFees,
					minimumDue: minimumDue
				};
		}

		this.context.source.addOrUpdateStepData(
			{
				newBalance: currentData.newBalance
					?? paymentDetailsData.remainingBalance,
				paymentDetails: currentData.paymentDetailsData
					?? paymentDetailsData,
				amount: currentData.amount,
				type: currentData.type ??
					InsuranceConstants.accountingTypes.premium
			});

		await this.performPostInitActions();

		this.context.source.wizardStepLoading = false;
	}

	/**
	 * Handles the validity changed event sent from the child dynamic
	 * formly component. This will update the validity of the form for
	 * action buttons.
	 *
	 * @async
	 * @param {boolean} isValid
	 * The validity of the current displayed step data set.
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public async validityChanged(
		isValid: boolean): Promise<void>
	{
		this.context.source.validStepChanged(isValid);
	}

	/**
	 * Loads the reference options for the reference field.
	 *
	 * @async
	 * @return {Promise<any[]>}
	 * The promise containing the list of reference options.
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public async loadReferenceOptions(): Promise<any[]>
	{
		if (AnyHelper.isNullOrWhitespace(
			this.contextData.transactionId)
			|| AnyHelper.isNullOrWhitespace(
				this.paymentAssociationSettings?.path)) {
			return [];
		}

		this.entityInstanceApiService.entityInstanceTypeGroup =
			`PolicyTermTransaction.${this.policyTermEntityInstance.data.productName}`;
		const transaction =
			await this.entityInstanceApiService.get(
				this.contextData.transactionId);

		return InsuranceHelper
			.loadPaymentReferenceOptions(
				transaction,
				this.paymentAssociationSettings);
	}

	/**
	 * Loads the transactions for the transaction field.
	 *
	 * @async
	 * @return {Promise<IEntityInstance[]>}
	 * The promise containing the list of transaction options.
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public async loadTransactions(): Promise<IEntityInstance[]>
	{
		this.entityInstanceApiService.entityInstanceTypeGroup =
			InsuranceConstants.insuranceEntityTypeGroups.policyTerms;

		return await this.entityInstanceApiService
			.getChildren(
				this.contextData.id,
				'EntityType.Name.StartsWith(\'PolicyTermTransaction.\') '
					+ 'and status ne \'Archived\' and status ne \'Obsolete\'',
				'Id desc',
				null,
				null,
				null);
	}

	/**
	 * Handles the transaction field change event.
	 *
	 * @param {FormlyFieldConfig} _field
	 * The formly field configuration.
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public onTransactionChange(
		_field: FormlyFieldConfig): void
	{
		setTimeout(() => {
			this.referenceFieldConfig
				.formControl
				.setValue(null);
			this.referenceFieldConfig
				.formControl
				.updateValueAndValidity();

			if (this.referenceFieldConfig
				.props
				.initializeDataOptions)
			{
				this.referenceFieldConfig
					.props
					.initializeDataOptions();
			}
		}, AppConstants.time.oneHundredMilliseconds);
	}

	/**
	 * Handles the post initialization action.
	 * This will create the dynamic formly layout for display component creation
	 * and permissions.
	 *
	 * @async
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	private async performPostInitActions(): Promise<void>
	{
		this.dynamicFormlyLayout =
			<FormlyFieldConfig[]>
			[
				{
					key: 'type',
					type: FormlyConstants.customControls.customSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Write Off Type',
						required: true,
						options:
							<IDropdownOption[]>
							[
								{
									label:
										InsuranceConstants.accountingTypes.premium,
									value:
										InsuranceConstants.accountingTypes.premium,
								},
								{
									label:
										InsuranceConstants.accountingTypes.fee,
									value:
										InsuranceConstants.accountingTypes.fee,
								},
								{
									label:
										InsuranceConstants.accountingTypes.tax,
									value:
										InsuranceConstants.accountingTypes.tax,
								}
							]
					}
				},
				{
					key: 'transactionId',
					type: FormlyConstants.customControls.customDataSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Transaction',
						placeholder: 'Select an Option',
						required: true,
						showClear: true,
						dataPromise:
							this.loadTransactions.bind(this),
						labelTemplate: '${item.data.transactionNumber}',
						valueTemplate: '${item.id}',
						change: this.onTransactionChange.bind(this)
					},
					expressions: {
						hide: () =>
							AnyHelper.isNullOrWhitespace(
								this.paymentAssociationSettings?.path)
					}
				},
				{
					key: 'referenceId',
					type: FormlyConstants.customControls.customDataSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Reference',
						placeholder: 'Select an Option',
						required: false,
						showClear: true,
						dataPromise:
							this.loadReferenceOptions.bind(this),
						labelTemplate: '${item.description}',
						valueTemplate: '${item.id}'
					},
					expressions: {
						hide: () =>
							AnyHelper.isNullOrWhitespace(
								this.paymentAssociationSettings?.path),
						'props.label': () =>
							this.paymentAssociationSettings?.label || 'Reference'
					}
				},
				{
					key: 'amount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Write Off Amount',
						useCurrency: true,
						change:
							(field: FormlyFieldConfig,
								_event: any) =>
							{
								const newBalance: number =
									this.context.source
										.activeMenuItem.currentData.data
										.paymentDetails.remainingBalance
									- (field.formControl.value || 0);

								this.dynamicFormlyLayout[4]
									.formControl.setValue(newBalance);
							}
					},
					validators: {
						positivePaymentAmount: {
							expression: ((
								control: UntypedFormControl,
								field: FormlyFieldConfig) =>
								this.positivePaymentAmountValidator(
									control,
									field)),
							message:
								'A value above zero dollars must be written off.'
						},
						allowedTotalWritten: {
							expression: ((
								control: UntypedFormControl,
								field: FormlyFieldConfig) =>
								this.allowedTotalRemainingBalanceValidator(
									control,
									field)),
							message:
								'A value less than the total remaining balance is'
								+ ' required.'
						}
					}
				},
				{
					key: 'newBalance',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'New Balance',
						disabled: true,
						useCurrency: true
					}
				}
			];
	}

	/**
	 * Validates the payment amount is positive
	 *
	 * @param {AbstractControl} control
	 * The form control to get the input value.
	 * @param {FormlyFieldConfig} _field
	 * The formly field configuration.
	 * @returns {boolean}
	 * The validation passed or failed.
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	private positivePaymentAmountValidator(
		control: AbstractControl,
		_field: FormlyFieldConfig): boolean
	{
		return !AnyHelper.isNullOrWhitespace(control.value)
			&& control.value > 0;
	}

	/**
	 * Validates the payment amount is less than the total remaining balance.
	 *
	 * @param {AbstractControl} control
	 * The form control to get the input value.
	 * @param {FormlyFieldConfig} _field
	 * The formly field configuration.
	 * @returns {boolean}
	 * The validation passed or failed.
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	private allowedTotalRemainingBalanceValidator(
		control: AbstractControl,
		_field: FormlyFieldConfig): boolean
	{
		const paymentDetails: any =
			this.context.source
				.activeMenuItem.currentData.data
				.paymentDetails;

		return control.value <= paymentDetails.remainingBalance;
	}
}